/**
 * useClickableEntity - Hook for making a card clickable to open a CRM slideout.
 *
 * Encapsulates the slideout-context lookup, memoised click/keydown handlers,
 * and accessible container props (role, tabIndex) that EntityCard and
 * ObjectMiniCard previously duplicated inline.
 *
 * Usage:
 *   const { isClickable, containerProps } = useClickableEntity(entityId);
 *   <div {...containerProps} className={cx(base, isClickable && 'cursor-pointer')}>
 */

import { useCallback, useMemo } from 'react';

import { useOpenSlideoutOptional } from '@/lib/crm/hooks/useOpenSlideout';

export interface UseClickableEntityResult {
  /** Whether the entity can be clicked (entityId present + slideout context available). */
  isClickable: boolean;
  /**
   * Spread these directly onto the container element.
   * Includes role, tabIndex, onClick, and onKeyDown — all undefined when not clickable.
   */
  containerProps: {
    role: 'button' | undefined;
    tabIndex: 0 | undefined;
    onClick: ((e: React.MouseEvent) => void) | undefined;
    onKeyDown: ((e: React.KeyboardEvent) => void) | undefined;
  };
}

/**
 * Makes an entity card clickable to open the CRM slideout panel.
 *
 * @param entityId - CRM entity ID. When undefined or when no slideout context
 *   is available, the card renders as a static element.
 */
export function useClickableEntity(
  entityId: string | undefined
): UseClickableEntityResult {
  // Origin-aware open (see useOpenSlideout): layers only from within a
  // rendered entity/thread slideout; replaces from the app or the proposal.
  const openSlideout = useOpenSlideoutOptional();

  const isClickable = !!entityId && !!openSlideout;

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (entityId && openSlideout) {
        e.stopPropagation();
        openSlideout(entityId);
      }
    },
    [entityId, openSlideout]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (entityId && openSlideout && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        e.stopPropagation();
        openSlideout(entityId);
      }
    },
    [entityId, openSlideout]
  );

  const containerProps = useMemo(
    () => ({
      role: isClickable ? ('button' as const) : undefined,
      tabIndex: isClickable ? (0 as const) : undefined,
      onClick: isClickable ? handleClick : undefined,
      onKeyDown: isClickable ? handleKeyDown : undefined,
    }),
    [isClickable, handleClick, handleKeyDown]
  );

  return { isClickable, containerProps };
}
